import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { NoddiCircularLoaderWithTrans } from '@components/Loading/NoddiCircularLoaderWithTrans';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { keepPreviousData } from '@tanstack/react-query';
import { noddiAsync, URLKeys } from 'noddi-async';
import { usePagination } from 'noddi-ui-web';
import type { ReactNode } from 'react';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';
import { BookingsList } from '../Bookings/BookingsList';

const PAGE_SIZE = 5;

type BookingsScope = { isUpcoming: true } | { isCompleted: true };

export const OrganizationMemberBookings = ({ userGroupId }: { userGroupId: number }) => (
  <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
    <MemberBookingsSection
      userGroupId={userGroupId}
      // The endpoint's upcoming filter already keeps what a customer may act on, including a booking
      // staff created that awaits their payment.
      scope={{ isUpcoming: true }}
      title={<Trans>Upcoming bookings</Trans>}
      emptyStateMessage={t`No upcoming bookings from you or for the cars you drive.`}
    />
    <MemberBookingsSection
      userGroupId={userGroupId}
      scope={{ isCompleted: true }}
      title={<Trans>Previous bookings</Trans>}
      emptyStateMessage={t`No previous bookings from you or for the cars you drive.`}
    />
  </div>
);

/** One list with its own loading and error state, so a failure in one never hides the other. */
const MemberBookingsSection = ({
  userGroupId,
  scope,
  title,
  emptyStateMessage
}: {
  userGroupId: number;
  scope: BookingsScope;
  title: ReactNode;
  emptyStateMessage: string;
}) => {
  const { currentBrand } = useCurrentBrand();
  const { pagination, setPagination, search } = usePagination({ initialPageSize: PAGE_SIZE });
  const {
    data: bookings,
    error,
    isFetching
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedBookingsForCustomer,
    input: {
      userGroupId,
      ...scope,
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
      brandDomains: [currentBrand]
    },
    queryConfig: { placeholderData: keepPreviousData }
  });

  return (
    <section className='flex flex-col gap-3'>
      <h2 className='text-xl font-bold text-primary md:text-3xl'>{title}</h2>
      {/* A failed refresh keeps the rows already on screen. */}
      {error && <ApiErrorMessageWithTrans error={error} />}
      {bookings ? (
        <BookingsList
          bookings={bookings}
          isFetching={isFetching}
          pagination={pagination}
          onPaginationChange={(nextPagination) => setPagination({ ...nextPagination, search })}
          emptyStateMessage={emptyStateMessage}
        />
      ) : (
        !error && <NoddiCircularLoaderWithTrans />
      )}
    </section>
  );
};
